import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  IconButton,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon
} from '@mui/material';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Refresh as RefreshIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';

interface MediaItem {
  id: string;
  filename: string;
  original_name: string;
  file_type: string | null;
  file_size: number;
  storage_url: string;
  is_approved: boolean;
  is_featured: boolean;
  featured_date?: string;
  uploaded_at: string;
  guest_name?: string;
  guest_id: string;
}

interface MediaStats {
  total: number;
  approved: number;
  pending: number;
  featured: number;
  totalSize: number;
}

export const MediaManagement: React.FC = () => {
  const [pendingMedia, setPendingMedia] = useState<MediaItem[]>([]);
  const [allMedia, setAllMedia] = useState<MediaItem[]>([]);
  const [stats, setStats] = useState<MediaStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'pending' | 'all'>('pending');
  const [filterStatus, setFilterStatus] = useState<'all' | 'approved' | 'featured'>('all');
  
  // Dialog states
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  
  // Upload states
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadLoading, setUploadLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    loadMediaData();
  }, []);

  useEffect(() => {
    // Reload data when view mode changes
    if (viewMode === 'all' && allMedia.length === 0) {
      loadMediaData();
    }
  }, [viewMode]);

  const loadMediaData = async () => {
    try {
      // Use the ApiService which has the correct API Gateway URL
      const { ApiService } = await import('../services/api');

      const [pendingResult, statsResult] = await Promise.all([
        ApiService.getPendingMedia(),
        ApiService.getMediaStats()
      ]);

      // Sort function - newest first
      const sortByDate = (a: MediaItem, b: MediaItem) => {
        const dateA = new Date(a.uploaded_at).getTime();
        const dateB = new Date(b.uploaded_at).getTime();
        return dateB - dateA; // Newest first
      };

      if (pendingResult.success) {
        const pending = pendingResult.data.media || [];
        setPendingMedia(pending.sort(sortByDate));
      }

      if (statsResult.success) {
        setStats(statsResult.data);
      }

      // Load all media if in 'all' view mode
      if (viewMode === 'all') {
        const allMediaResult = await ApiService.getMedia();
        if (allMediaResult.success) {
          const all = allMediaResult.data || [];
          setAllMedia(all.sort(sortByDate));
        }
      }

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load media data');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveMedia = async (mediaId: string) => {
    setActionLoading(mediaId);
    try {
      const token = localStorage.getItem('authToken');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
      const response = await fetch(`${API_BASE_URL}/admin/media/${mediaId}/approve`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to approve media');
      }

      loadMediaData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve media');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectMedia = async (mediaId: string) => {
    if (!window.confirm('Are you sure you want to delete this media? This action cannot be undone.')) {
      return;
    }
    
    setActionLoading(mediaId);
    try {
      const token = localStorage.getItem('authToken');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
      const response = await fetch(`${API_BASE_URL}/admin/media/${mediaId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to reject media');
      }

      loadMediaData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject media');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleFeatured = async (mediaId: string, featured: boolean) => {
    setActionLoading(mediaId);
    try {
      const token = localStorage.getItem('authToken');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
      const response = await fetch(`${API_BASE_URL}/admin/media/${mediaId}/featured`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          featured,
          featuredDate: featured ? new Date().toISOString() : null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to update featured status');
      }

      loadMediaData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update featured status');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRotateFeatured = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/admin/media/rotate-featured', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          count: 3
        })
      });

      if (!response.ok) {
        throw new Error('Failed to rotate featured media');
      }

      loadMediaData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rotate featured media');
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        setError('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const handleUploadMedia = async () => {
    if (!selectedFile) return;
    
    setUploadLoading(true);
    try {
      const { ApiService } = await import('../services/api');
      
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const result = await ApiService.uploadMedia(formData);
      
      if (result.success) {
        setUploadDialogOpen(false);
        setSelectedFile(null);
        loadMediaData();
      } else {
        throw new Error(result.error || 'Failed to upload media');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload media');
    } finally {
      setUploadLoading(false);
    }
  };

  const handlePreviewMedia = (media: MediaItem) => {
    setSelectedMedia(media);
    setPreviewDialogOpen(true);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getDisplayMedia = () => {
    if (viewMode === 'pending') {
      return pendingMedia;
    }
    
    let media = allMedia;
    if (filterStatus === 'approved') {
      media = media.filter(m => m.is_approved);
    } else if (filterStatus === 'featured') {
      media = media.filter(m => m.is_featured);
    }
    
    return media;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  const displayMedia = getDisplayMedia();

  const speedDialActions = [
    { icon: <CloudUploadIcon />, name: 'Upload Media', action: () => setUploadDialogOpen(true) },
    { icon: <RefreshIcon />, name: 'Refresh', action: loadMediaData },
    { icon: <StarIcon />, name: 'Rotate Featured', action: handleRotateFeatured },
  ];

  return (
    <Box sx={{ pb: isMobile ? 8 : 0 }}>
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems={isMobile ? "flex-start" : "center"} 
        mb={3}
        flexDirection={isMobile ? "column" : "row"}
        gap={isMobile ? 2 : 0}
      >
        <Typography variant="h5">
          Media Management
        </Typography>
        
        {isMobile ? (
          <Box display="flex" flexDirection="column" gap={2} width="100%">
            <FormControlLabel
              control={
                <Switch
                  checked={viewMode === 'all'}
                  onChange={(e) => {
                    setViewMode(e.target.checked ? 'all' : 'pending');
                    if (e.target.checked) {
                      loadMediaData();
                    }
                  }}
                />
              }
              label="Show All Media"
            />
          </Box>
        ) : (
          <Box display="flex" gap={2} alignItems="center">
            <FormControlLabel
              control={
                <Switch
                  checked={viewMode === 'all'}
                  onChange={(e) => {
                    setViewMode(e.target.checked ? 'all' : 'pending');
                    if (e.target.checked) {
                      loadMediaData();
                    }
                  }}
                />
              }
              label="Show All Media"
            />
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={() => setUploadDialogOpen(true)}
              color="primary"
            >
              Upload Media
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadMediaData}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              onClick={handleRotateFeatured}
            >
              Rotate Featured
            </Button>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={isSmallMobile ? 1 : 2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: isSmallMobile ? 1.5 : 2 }}>
                <Typography variant={isSmallMobile ? "subtitle2" : "h6"} color="primary">
                  Total Media
                </Typography>
                <Typography variant={isSmallMobile ? "h5" : "h4"}>{stats.total}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(stats.totalSize)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: isSmallMobile ? 1.5 : 2 }}>
                <Typography variant={isSmallMobile ? "subtitle2" : "h6"} color="success.main">
                  Approved
                </Typography>
                <Typography variant={isSmallMobile ? "h5" : "h4"}>{stats.approved}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {stats.total > 0 ? Math.round((stats.approved / stats.total) * 100) : 0}% of total
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: isSmallMobile ? 1.5 : 2 }}>
                <Typography variant={isSmallMobile ? "subtitle2" : "h6"} color="warning.main">
                  Pending
                </Typography>
                <Typography variant={isSmallMobile ? "h5" : "h4"}>{stats.pending}</Typography>
                <Typography variant="caption" color="text.secondary">
                  awaiting approval
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={3}>
            <Card>
              <CardContent sx={{ p: isSmallMobile ? 1.5 : 2 }}>
                <Typography variant={isSmallMobile ? "subtitle2" : "h6"} color="secondary">
                  Featured
                </Typography>
                <Typography variant={isSmallMobile ? "h5" : "h4"}>{stats.featured}</Typography>
                <Typography variant="caption" color="text.secondary">
                  currently featured
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filter Controls */}
      {viewMode === 'all' && (
        <Box sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Filter</InputLabel>
            <Select
              value={filterStatus}
              label="Filter"
              onChange={(e) => setFilterStatus(e.target.value as any)}
            >
              <MenuItem value="all">All Media</MenuItem>
              <MenuItem value="approved">Approved Only</MenuItem>
              <MenuItem value="featured">Featured Only</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Media Grid */}
      <Grid container spacing={isSmallMobile ? 1 : 2}>
        {displayMedia.map((media) => (
          <Grid item xs={6} sm={4} md={3} lg={2} key={media.id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ position: 'relative', paddingTop: '100%', backgroundColor: '#f5f5f5' }}>
                <CardMedia
                  component={media.file_type?.startsWith('video/') ? 'video' : 'img'}
                  image={media.storage_url}
                  title={media.original_name}
                  controls={media.file_type?.startsWith('video/')}
                  loading="lazy"
                  sx={{ 
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    cursor: 'pointer',
                    backgroundColor: '#f5f5f5'
                  }}
                  onClick={() => handlePreviewMedia(media)}
                />
                
                {/* Status badges */}
                <Box sx={{ position: 'absolute', top: 8, left: 8, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {media.is_approved ? (
                    <Chip label="✓" size="small" color="success" sx={{ minWidth: 'auto', height: 20 }} />
                  ) : (
                    <Chip label="⏳" size="small" color="warning" sx={{ minWidth: 'auto', height: 20 }} />
                  )}
                  
                  {media.is_featured && (
                    <Chip label="⭐" size="small" color="secondary" sx={{ minWidth: 'auto', height: 20 }} />
                  )}
                </Box>
              </Box>
              
              <CardContent sx={{ p: 1, flexGrow: 1 }}>
                <Typography variant="caption" noWrap display="block" title={media.original_name}>
                  {media.original_name}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap display="block">
                  {media.guest_name || 'Unknown'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(media.file_size)}
                </Typography>
              </CardContent>
              
              <CardActions sx={{ p: 1, pt: 0, justifyContent: 'center' }}>
                {!media.is_approved && (
                  <Box display="flex" gap={0.5}>
                    <IconButton
                      size="small"
                      color="success"
                      onClick={() => handleApproveMedia(media.id)}
                      disabled={actionLoading === media.id}
                      sx={{ minWidth: 32, minHeight: 32 }}
                    >
                      {actionLoading === media.id ? <CircularProgress size={16} /> : <CheckIcon fontSize="small" />}
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRejectMedia(media.id)}
                      disabled={actionLoading === media.id}
                      sx={{ minWidth: 32, minHeight: 32 }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
                
                {media.is_approved && (
                  <Box display="flex" gap={0.5}>
                    <IconButton
                      size="small"
                      color="secondary"
                      onClick={() => handleToggleFeatured(media.id, !media.is_featured)}
                      disabled={actionLoading === media.id}
                      sx={{ minWidth: 32, minHeight: 32 }}
                    >
                      {actionLoading === media.id ? (
                        <CircularProgress size={16} />
                      ) : media.is_featured ? (
                        <StarIcon fontSize="small" />
                      ) : (
                        <StarBorderIcon fontSize="small" />
                      )}
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRejectMedia(media.id)}
                      disabled={actionLoading === media.id}
                      sx={{ minWidth: 32, minHeight: 32 }}
                      title="Delete"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {displayMedia.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="body1" color="text.secondary">
            {viewMode === 'pending' ? 'No pending media items' : 'No media items found'}
          </Typography>
        </Box>
      )}

      {/* Preview Dialog */}
      <Dialog 
        open={previewDialogOpen} 
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedMedia?.original_name}
        </DialogTitle>
        <DialogContent>
          {selectedMedia && (
            <Box>
              {selectedMedia.file_type?.startsWith('video/') ? (
                <video
                  src={selectedMedia.storage_url}
                  controls
                  style={{ width: '100%', maxHeight: '400px' }}
                />
              ) : (
                <img
                  src={selectedMedia.storage_url}
                  alt={selectedMedia.original_name}
                  loading="lazy"
                  style={{ width: '100%', maxHeight: '400px', objectFit: 'contain', backgroundColor: '#f5f5f5' }}
                />
              )}
              
              <Box mt={2}>
                <Typography variant="body2">
                  <strong>Uploaded by:</strong> {selectedMedia.guest_name || 'Unknown Guest'}
                </Typography>
                <Typography variant="body2">
                  <strong>File size:</strong> {formatFileSize(selectedMedia.file_size)}
                </Typography>
                <Typography variant="body2">
                  <strong>Uploaded:</strong> {new Date(selectedMedia.uploaded_at).toLocaleString()}
                </Typography>
                <Typography variant="body2">
                  <strong>Status:</strong> {selectedMedia.is_approved ? 'Approved' : 'Pending'}
                </Typography>
                {selectedMedia.is_featured && (
                  <Typography variant="body2">
                    <strong>Featured since:</strong> {selectedMedia.featured_date ? new Date(selectedMedia.featured_date).toLocaleDateString() : 'Unknown'}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog 
        open={uploadDialogOpen} 
        onClose={() => !uploadLoading && setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Media</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="media-upload-input"
            />
            <label htmlFor="media-upload-input">
              <Button
                variant="outlined"
                component="span"
                startIcon={<CloudUploadIcon />}
                fullWidth
                sx={{ mb: 2 }}
              >
                Select Image File
              </Button>
            </label>
            
            {selectedFile && (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Selected:</strong> {selectedFile.name}
                  </Typography>
                  <Typography variant="caption">
                    Size: {formatFileSize(selectedFile.size)}
                  </Typography>
                </Alert>
                
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Supported formats: JPEG, PNG, GIF, WebP (max 10MB)
                </Typography>
              </Box>
            )}
            
            {!selectedFile && (
              <Typography variant="body2" color="text.secondary" align="center">
                Select an image file to upload to the media library
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setUploadDialogOpen(false);
              setSelectedFile(null);
            }}
            disabled={uploadLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUploadMedia}
            variant="contained"
            disabled={!selectedFile || uploadLoading}
            startIcon={uploadLoading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
          >
            {uploadLoading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mobile Speed Dial for Actions */}
      {isMobile && (
        <SpeedDial
          ariaLabel="Media actions"
          sx={{ position: 'fixed', bottom: 80, right: 16 }}
          icon={<SpeedDialIcon />}
          open={speedDialOpen}
          onClose={() => setSpeedDialOpen(false)}
          onOpen={() => setSpeedDialOpen(true)}
        >
          {speedDialActions.map((action) => (
            <SpeedDialAction
              key={action.name}
              icon={action.icon}
              tooltipTitle={action.name}
              onClick={() => {
                action.action();
                setSpeedDialOpen(false);
              }}
            />
          ))}
        </SpeedDial>
      )}
    </Box>
  );
};